// Der Abschnitt "Sperren" des Kontaktdetails (ST-CAM-001 C1a/C1b; Michael E5,
// E2 b, E7 a; Vorgaben DESIGN 15./16.10.): was für diesen Kontakt gesperrt ist,
// "Sperre eintragen" als Abschnittsaktion, "Aufheben …" an der Zeile mit
// Pflichtgrund und darunter zugeklappt die aufgehobenen Sperren.
//
// DREI ZUSTÄNDE, keiner verkleidet sich als ein anderer (NFR-UX-005): geladen
// mit Sperren, geladen ohne ("Keine Sperre eingetragen."), nicht geladen -- dann
// NIE "keine Sperre", denn das hieße fälschlich "frei".
//
// EINE STATUSREGION für Ergebnisse; der Fokus geht nach dem Eintragen dorthin
// (Muster Rolle im Haus). Der Dialog ist nur eingehängt, wenn er offen ist.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Select,
  Subtitle1,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../../access.ts';
import type { HausArt } from '../../haus-art.ts';
import { newClientRequestId } from '../../client-request.ts';
import { groupContactPoints } from '../../contact-points.ts';
import {
  addedAnnouncement,
  additionalSuppressions,
  addSuppression,
  CHANNEL_WORD,
  DIALOG_NOTE,
  endedHeading,
  endedMeta,
  endSuppression,
  fetchEndedSuppressions,
  fetchSuppressions,
  isLiftable,
  liftOffered,
  LIFT_CLOSES_DIALOG,
  LIFT_NOTE,
  LIFT_OUTDATED_MESSAGE,
  LIFT_REASON_REQUIRED,
  LIFT_REASONS,
  LIFT_UNREACHABLE_MESSAGE,
  liftButtonName,
  liftedAnnouncement,
  liftedLine,
  liftReasonsFor,
  ONLY_PRIVACY_LIFTS,
  type EndedSuppressionView,
  type LiftReason,
  formatSuppressionDate,
  INCOMPLETE_LINE_HINT,
  truncatedWaysNote,
  lineHint,
  NO_SUPPRESSION_TEXT,
  REASONS,
  scopeWords,
  SUPPRESSIONS_LOAD_FAILED,
  suppressionLine,
  suppressionMeta,
  SUPPRESSION_MESSAGES,
  UNREACHABLE_MESSAGE,
  type SuppressionChannel,
  type SuppressionReason,
  type SuppressionScope,
  type SuppressionsData,
  type SuppressionView,
} from '../../communication-suppressions.ts';
import { usageLabel, type ContactPointView, type PartyDetail } from '../../parties.ts';
import { authorLabel } from '../../interactions.ts';
import { AbbrechenKnopf, NebenKnopf } from '../../shell/nebenknopf.tsx';
import { Zustand } from '../../shell/zustand.tsx';
import { PlusSymbol } from './plus-symbol.tsx';
import { useKontaktStyles } from './stile.ts';

const useStyles = makeStyles({
  zeile: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start' },
  symbol: { flexShrink: 0, marginTop: '0.2rem', color: tokens.colorPaletteDarkOrangeForeground1 },
  text: { display: 'flex', flexDirection: 'column', minWidth: 0, overflowWrap: 'anywhere' },
  meta: { color: tokens.colorNeutralForeground2 },
  liste: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  kanal: { display: 'flex', flexWrap: 'wrap', columnGap: '1rem' },
  grundHinweis: { display: 'block', color: tokens.colorNeutralForeground2 },
  dialogInhalt: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  wegAuswahl: { maxWidth: '100%', minWidth: 0, '& select': { maxWidth: '100%' } },
  hinweisZeile: { display: 'flex', gap: '0.25rem', alignItems: 'flex-start', color: tokens.colorNeutralForeground2 },
  // Der Knopf rechts; bei 320 px rutscht er unter den Text, statt ihn zu quetschen.
  zeileMitAktion: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'flex-start' },
  aktion: { marginLeft: 'auto', flexShrink: 0 },
  inhalt: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flex: '1 1 14rem', minWidth: 0 },
});

/** Das Warnsymbol; die Bedeutung trägt das Wort "gesperrt" daneben (WCAG 1.4.1). */
function WarnSymbol({ className }: { readonly className?: string }): ReactElement {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M12 3 2.5 20h19z" />
      <path d="M12 10v4.5M12 17.2v.3" strokeLinecap="round" />
    </svg>
  );
}

// --- Laden -------------------------------------------------------------------

export interface SperrenStand {
  readonly status: 'laedt' | 'geladen' | 'fehler';
  readonly data: SuppressionsData | undefined;
  readonly laden: () => Promise<void>;
}

export function useSperren({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
}): SperrenStand {
  const [status, setStatus] = useState<SperrenStand['status']>('laedt');
  const [data, setData] = useState<SuppressionsData | undefined>(undefined);
  const laufRef = useRef(0);
  const laden = useCallback(async () => {
    const lauf = laufRef.current + 1;
    laufRef.current = lauf;
    setStatus('laedt');
    try {
      const token = await accessToken();
      if (laufRef.current !== lauf) return;
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchSuppressions({ baseUrl: apiBaseUrl, accessToken: token, partyId });
      if (laufRef.current !== lauf) return;
      if (result.outcome === 'loaded') {
        setData(result.data);
        setStatus('geladen');
      } else {
        setStatus('fehler');
      }
    } catch {
      if (laufRef.current === lauf) setStatus('fehler');
    }
  }, [accessToken, apiBaseUrl, onReauthenticate, partyId]);
  useEffect(() => {
    void laden();
    return () => {
      laufRef.current += 1;
    };
  }, [laden]);
  return { status, data, laden };
}

// --- Hinweis an der Zeile (Kontaktwege, Anschriften) --------------------------

/**
 * "⚠ Gesperrt – <Grund>", bei mehreren " · + N weitere" (vorgelesen "und N weitere").
 * Sind die Sperren nicht vollständig geladen, sagt eine Zeile ohne bekannte Sperre das,
 * statt still "frei" zu wirken (fail-closed).
 */
export function SperrHinweis({ treffer: lage }: { readonly treffer: SperrLage }): ReactElement | null {
  const own = useStyles();
  const kontakt = useKontaktStyles();
  const hinweis = lineHint(lage.treffer);
  if (hinweis === undefined) {
    return lage.unvollstaendig ? (
      <Caption1 className={own.hinweisZeile}>
        <WarnSymbol className={own.symbol} />
        <span>{INCOMPLETE_LINE_HINT}</span>
      </Caption1>
    ) : null;
  }
  return (
    <Caption1 className={own.hinweisZeile}>
      <WarnSymbol className={own.symbol} />
      <span>
        {hinweis.text}
        {hinweis.weitere > 0 ? (
          <>
            <span aria-hidden="true"> · + {hinweis.weitere} weitere</span>
            <span className={kontakt.nurVorgelesen}> und {hinweis.weitere} weitere</span>
          </>
        ) : undefined}
      </span>
    </Caption1>
  );
}

// --- Der Abschnitt -------------------------------------------------------------

const OEFFNER_ID = 'sperre-eintragen';
const STATUS_ID = 'kontakt-sperren-status';

export function Sperren({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  party,
  stand,
  nurLesen = false,
  onKontaktNeu,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly party: PartyDetail;
  readonly stand: SperrenStand;
  /** Der Kontakt ist nicht beschreibbar: kein Eintragen. */
  readonly nurLesen?: boolean;
  /** Die Kontaktwege neu lesen (der gewählte Weg ist weg, oder der Kontakt nicht mehr beschreibbar). */
  readonly onKontaktNeu: () => void;
}): ReactElement {
  const own = useStyles();
  const kontakt = useKontaktStyles();
  const [offen, setOffen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  const [meldung, setMeldung] = useState<PlainMessage | undefined>(undefined);
  const [fokusStatus, setFokusStatus] = useState(0);
  const statusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (fokusStatus > 0) statusRef.current?.focus();
  }, [fokusStatus]);

  const werte = new Map<string, string>();
  for (const weg of [...party.emailAddresses, ...party.phoneNumbers]) werte.set(weg.contactPointId, weg.value);
  const wertDesWegs = (contactPointId: string): string | undefined => werte.get(contactPointId);

  const schliessen = (): void => {
    setOffen(false);
    window.setTimeout(() => document.getElementById(OEFFNER_ID)?.focus(), 0);
  };

  const { status, data } = stand;
  const sperren = data?.suppressions ?? [];
  // Kommt man über "Sperren" aus einer Warnleiste (#sperren), steht der Abschnitt nach dem Laden im Blick.
  const angesprungen = useRef(false);
  useEffect(() => {
    if (status !== 'geladen' || angesprungen.current || window.location.hash !== '#sperren') return;
    angesprungen.current = true;
    const kopf = document.getElementById('sperren');
    kopf?.scrollIntoView({ block: 'start' });
    // Auch der Fokus springt, sonst bliebe er nach dem Seitenwechsel am Anfang (WCAG 2.4.3).
    kopf?.focus({ preventScroll: true });
  }, [status]);
  const weitere = additionalSuppressions(data?.additionalSuppressionCount ?? 0);

  const [aufheben, setAufheben] = useState<SuppressionView | undefined>(undefined);
  const aufhebenSchliessen = (id: string): void => {
    setAufheben(undefined);
    window.setTimeout(() => document.getElementById(aufhebenKnopfId(id))?.focus(), 0);
  };
  const aufgehobene = useAufgehobene({ apiBaseUrl, accessToken, partyId: party.partyId, stand });

  return (
    <section className={kontakt.gruppe} aria-labelledby="sperren">
      <div className={kontakt.abschnittKopf} data-abschnittkopf="">
        <Subtitle1 as="h2" id="sperren" tabIndex={-1}>
          Sperren
        </Subtitle1>
        {status === 'geladen' && !nurLesen ? (
          <Button
            id={OEFFNER_ID}
            appearance="subtle"
            size="small"
            className={kontakt.abschnittPlus}
            icon={<PlusSymbol />}
            aria-label="Sperre eintragen"
            onClick={() => {
              setBestaetigung(undefined);
              setMeldung(undefined);
              setOffen(true);
            }}
          >
            Sperre
          </Button>
        ) : undefined}
      </div>

      {status === 'laedt' ? <Caption1 className={kontakt.hinweis}>Sperren werden geladen …</Caption1> : undefined}
      {status === 'geladen' && sperren.length === 0 ? <Caption1 className={kontakt.hinweis}>{NO_SUPPRESSION_TEXT}</Caption1> : undefined}
      {status === 'geladen' && sperren.length > 0 ? (
        <ul className={own.liste} aria-label="Sperren dieses Kontakts">
          {sperren.map((sperre) => {
            const zeile = suppressionLine(sperre, wertDesWegs);
            const aufhebbar = isLiftable(sperre.reasonCode);
            return (
              <li key={sperre.suppressionId} className={own.zeileMitAktion}>
                <span className={own.inhalt}>
                  <WarnSymbol className={own.symbol} />
                  <span className={own.text}>
                    <Body1>{zeile}</Body1>
                    <Caption1 className={own.meta}>{suppressionMeta(sperre, formatSuppressionDate)}</Caption1>
                    {/* Widerspruch und Rechtliche Sperre (E7 a): kein Knopf, auch kein gesperrter; nur dieser Satz. */}
                    {aufhebbar ? undefined : <Caption1 className={own.meta}>{ONLY_PRIVACY_LIFTS}</Caption1>}
                  </span>
                </span>
                {liftOffered(sperre) && !nurLesen ? (
                  <NebenKnopf
                    id={aufhebenKnopfId(sperre.suppressionId)}
                    size="small"
                    className={own.aktion}
                    aria-label={liftButtonName(zeile)}
                    onClick={() => {
                      setBestaetigung(undefined);
                      setMeldung(undefined);
                      setAufheben(sperre);
                    }}
                  >
                    Aufheben …
                  </NebenKnopf>
                ) : undefined}
              </li>
            );
          })}
        </ul>
      ) : undefined}
      {status === 'geladen' && weitere !== undefined ? <Caption1 className={kontakt.hinweis}>{weitere}</Caption1> : undefined}

      {/* Zugeklappt wie "Früher vergebene Stichworte"; fehlt ohne aufgehobene Sperren (A5). */}
      {aufgehobene !== undefined && aufgehobene.zeilen.length > 0 ? (
        <Accordion collapsible>
          <AccordionItem value="aufgehobene-sperren">
            <AccordionHeader>{endedHeading(aufgehobene.zeilen.length, aufgehobene.weitere)}</AccordionHeader>
            <AccordionPanel>
              <ul className={own.liste} aria-label="Aufgehobene Sperren">
                {aufgehobene.zeilen.map((sperre) => (
                  <li key={sperre.suppressionId} className={own.text}>
                    <Body1>{liftedLine(sperre, wertDesWegs)}</Body1>
                    {/* Wer, als sichtbarer Text wie im Verlauf; kein Name (Michael R3). */}
                    <Caption1 className={own.meta}>{endedMeta(sperre, formatSuppressionDate, (byMe) => authorLabel(byMe))}</Caption1>
                  </li>
                ))}
              </ul>
              {aufgehobene.weitere > 0 ? <Caption1 className={kontakt.hinweis}>{`und ${aufgehobene.weitere} weitere`}</Caption1> : undefined}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : undefined}

      <div ref={statusRef} id={STATUS_ID} tabIndex={-1} role="status" aria-live="polite" aria-label="Status: Sperren" className={kontakt.hinweis}>
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Gespeichert</MessageBarTitle>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {meldung !== undefined ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>{meldung.headline}</MessageBarTitle>
              <div>{meldung.explanation}</div>
              <div>{meldung.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {status === 'fehler' ? (
          <Zustand
            art="fehler"
            titel={SUPPRESSIONS_LOAD_FAILED.headline}
            saetze={[SUPPRESSIONS_LOAD_FAILED.explanation, SUPPRESSIONS_LOAD_FAILED.nextStep]}
            eingebettet
            haupt={{ label: 'Sperren erneut laden', onClick: () => void stand.laden() }}
          />
        ) : undefined}
      </div>

      {offen && data !== undefined ? (
        <SperreEintragen
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          party={party}
          data={data}
          onSchliessen={schliessen}
          onKontaktNeu={onKontaktNeu}
          onEingetragen={(satz, art) => {
            setOffen(false);
            if (art === 'neu') setBestaetigung(satz);
            else setMeldung({ headline: satz, explanation: '', nextStep: '', retryable: false });
            setFokusStatus((zahl) => zahl + 1);
            void stand.laden();
          }}
          onLadenNoetig={() => void stand.laden()}
        />
      ) : undefined}
      {aufheben !== undefined && aufheben.lockVersion !== undefined ? (
        <SperreAufheben
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          partyId={party.partyId}
          sperre={aufheben}
          lockVersion={aufheben.lockVersion}
          hausArt={data?.houseScope.available === true ? data.houseScope.kind : null}
          zeile={suppressionLine(aufheben, wertDesWegs)}
          meta={suppressionMeta(aufheben, formatSuppressionDate)}
          onSchliessen={() => aufhebenSchliessen(aufheben.suppressionId)}
          onKontaktNeu={onKontaktNeu}
          onFertig={(ergebnis) => {
            setAufheben(undefined);
            if (ergebnis.art === 'aufgehoben') setBestaetigung(ergebnis.satz);
            else setMeldung(ergebnis.meldung);
            setFokusStatus((zahl) => zahl + 1);
            void stand.laden();
          }}
        />
      ) : undefined}
    </section>
  );
}

// --- Aufgehobene Sperren -------------------------------------------------------

const aufhebenKnopfId = (suppressionId: string): string => `sperre-aufheben-${suppressionId}`;

/**
 * Liest die aufgehobenen Sperren nach jedem geladenen Stand der offenen. Nur
 * Anzeige: ein Fehler zeigt nichts (die offenen Sperren tragen die Aussage).
 */
function useAufgehobene({
  apiBaseUrl,
  accessToken,
  partyId,
  stand,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly partyId: string;
  readonly stand: SperrenStand;
}): { readonly zeilen: readonly EndedSuppressionView[]; readonly weitere: number } | undefined {
  const [ergebnis, setErgebnis] = useState<{ readonly zeilen: readonly EndedSuppressionView[]; readonly weitere: number } | undefined>(undefined);
  const { status, data } = stand;
  useEffect(() => {
    if (status !== 'geladen') return undefined;
    let gueltig = true;
    void (async () => {
      try {
        const token = await accessToken();
        if (!gueltig || token === undefined) return;
        const gelesen = await fetchEndedSuppressions({ baseUrl: apiBaseUrl, accessToken: token, partyId });
        if (!gueltig) return;
        setErgebnis(gelesen.outcome === 'loaded' ? { zeilen: gelesen.suppressions, weitere: gelesen.additionalSuppressionCount } : undefined);
      } catch {
        if (gueltig) setErgebnis(undefined);
      }
    })();
    return () => {
      gueltig = false;
    };
  }, [apiBaseUrl, accessToken, partyId, status, data]);
  return ergebnis;
}

// --- Der Dialog "Sperre aufheben" ----------------------------------------------

type AufhebenErgebnis = { readonly art: 'aufgehoben'; readonly satz: string } | { readonly art: 'meldung'; readonly meldung: PlainMessage };

function SperreAufheben({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  sperre,
  lockVersion,
  hausArt,
  zeile,
  meta,
  onSchliessen,
  onKontaktNeu,
  onFertig,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
  readonly sperre: SuppressionView;
  readonly lockVersion: number;
  readonly hausArt: HausArt | null;
  readonly zeile: string;
  readonly meta: string;
  readonly onSchliessen: () => void;
  readonly onKontaktNeu: () => void;
  /** Erfolg oder eine Antwort, nach der der Dialog schließt und der Abschnitt neu lädt (A4, A6). */
  readonly onFertig: (ergebnis: AufhebenErgebnis) => void;
}): ReactElement {
  const own = useStyles();
  const [grund, setGrund] = useState<LiftReason | undefined>(undefined);
  const [fehler, setFehler] = useState<string | undefined>(undefined);
  const [meldung, setMeldung] = useState<PlainMessage | undefined>(undefined);
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const meldungRef = useRef<HTMLDivElement | null>(null);
  const [fokusMeldung, setFokusMeldung] = useState(0);
  useEffect(() => {
    if (fokusMeldung > 0) meldungRef.current?.focus();
  }, [fokusMeldung]);
  const [fokusGrund, setFokusGrund] = useState(0);
  useEffect(() => {
    if (fokusGrund > 0) document.getElementById('sperre-aufheben-grund')?.querySelector<HTMLInputElement>('input')?.focus();
  }, [fokusGrund]);
  const gruende = liftReasonsFor(sperre.reasonCode);
  const linie = zeile.replace(' gesperrt – ', ' – ');

  const absenden = async (): Promise<void> => {
    if (grund === undefined) {
      setFehler(LIFT_REASON_REQUIRED);
      setFokusGrund((zahl) => zahl + 1);
      return;
    }
    setBusy(true);
    setMeldung(undefined);
    setOffen(false);
    let ergebnis;
    try {
      const token = await accessToken();
      if (token === undefined) {
        setBusy(false);
        onReauthenticate();
        return;
      }
      ergebnis = await endSuppression({ baseUrl: apiBaseUrl, accessToken: token, partyId, suppressionId: sperre.suppressionId, endReasonCode: grund, lockVersion, art: hausArt });
    } catch {
      // Ob der Server aufgehoben hat, ist offen; der Stand klärt es.
      setBusy(false);
      setMeldung(LIFT_UNREACHABLE_MESSAGE);
      setOffen(true);
      setFokusMeldung((zahl) => zahl + 1);
      return;
    }
    setBusy(false);
    if (ergebnis.outcome === 'applied') {
      onFertig({ art: 'aufgehoben', satz: liftedAnnouncement(linie, grund) });
      return;
    }
    if (ergebnis.outcome === 'invalid') {
      // Ein Feldfehler an der Version heißt: der Stand ist alt.
      if (ergebnis.errors.some((zeileFehler) => zeileFehler.fieldPath === 'lockVersion')) {
        onFertig({ art: 'meldung', meldung: LIFT_OUTDATED_MESSAGE });
        return;
      }
      setFehler(LIFT_REASON_REQUIRED);
      setFokusGrund((zahl) => zahl + 1);
      return;
    }
    const grundcode = ergebnis.outcome === 'conflict' ? ergebnis.reasonCode : undefined;
    if (ergebnis.outcome === 'not-found' || (grundcode !== undefined && LIFT_CLOSES_DIALOG.has(grundcode))) {
      onFertig({ art: 'meldung', meldung: ergebnis.message });
      return;
    }
    if (grundcode === 'party_not_writable') onKontaktNeu();
    setMeldung(ergebnis.message);
    setFokusMeldung((zahl) => zahl + 1);
  };

  return (
    <Dialog open modalType="modal" onOpenChange={(_, daten) => (daten.open ? undefined : onSchliessen())}>
      <DialogSurface aria-describedby={undefined}>
        <DialogBody>
          <DialogTitle>Sperre aufheben</DialogTitle>
          <DialogContent className={own.dialogInhalt}>
            {meldung !== undefined ? (
              <div ref={meldungRef} tabIndex={-1} role="alert">
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <MessageBarTitle>{meldung.headline}</MessageBarTitle>
                    {meldung.explanation === '' ? undefined : <div>{meldung.explanation}</div>}
                    {meldung.nextStep === '' ? undefined : <div>{meldung.nextStep}</div>}
                  </MessageBarBody>
                </MessageBar>
              </div>
            ) : undefined}
            {offen ? (
              <div>
                <NebenKnopf onClick={() => onFertig({ art: 'meldung', meldung: LIFT_UNREACHABLE_MESSAGE })}>Sperren erneut laden</NebenKnopf>
              </div>
            ) : undefined}
            {/* Die Zeile wie im Abschnitt, zum Wiedererkennen. */}
            <div className={own.zeile}>
              <WarnSymbol className={own.symbol} />
              <span className={own.text}>
                <Body1>{zeile}</Body1>
                <Caption1 className={own.meta}>{meta}</Caption1>
              </span>
            </div>
            <Field label="Warum wird sie aufgehoben?" required validationMessage={fehler ?? null} validationState={fehler === undefined ? 'none' : 'error'}>
              <RadioGroup
                id="sperre-aufheben-grund"
                value={grund ?? ''}
                onChange={(_, daten) => {
                  setGrund(daten.value as LiftReason);
                  setFehler(undefined);
                }}
              >
                {gruende.map((code) => (
                  <Radio
                    key={code}
                    value={code}
                    disabled={busy}
                    label={
                      <>
                        {LIFT_REASONS[code].wort}
                        <Caption1 className={own.grundHinweis}>{LIFT_REASONS[code].hinweis}</Caption1>
                      </>
                    }
                  />
                ))}
              </RadioGroup>
            </Field>
            <Caption1 className={own.hinweisZeile}>
              <span aria-hidden="true">ⓘ</span>
              <span>{LIFT_NOTE}</span>
            </Caption1>
          </DialogContent>
          <DialogActions>
            <AbbrechenKnopf disabled={busy} onClick={onSchliessen} />
            <Button appearance="primary" disabledFocusable={busy} onClick={() => void absenden()}>
              {busy ? 'Wird aufgehoben …' : 'Sperre aufheben'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// --- Der Dialog "Sperre eintragen" ----------------------------------------------

const KANAELE: readonly SuppressionChannel[] = ['email', 'phone', 'postal'];
const ALLE = '';

interface WegOption {
  readonly contactPointId: string;
  readonly contactPointUsageId: string;
  readonly text: string;
}

function wegOptionen(wege: readonly ContactPointView[]): readonly WegOption[] {
  return groupContactPoints(wege).map((gruppe) => ({
    contactPointId: gruppe.contactPointId,
    contactPointUsageId: gruppe.first.contactPointUsageId,
    text: `${gruppe.value} (${gruppe.usages.map((usage) => usageLabel(usage.usageCode)).join(', ')})`,
  }));
}

function SperreEintragen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  party,
  data,
  onSchliessen,
  onKontaktNeu,
  onEingetragen,
  onLadenNoetig,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly party: PartyDetail;
  readonly data: SuppressionsData;
  readonly onSchliessen: () => void;
  readonly onKontaktNeu: () => void;
  readonly onEingetragen: (satz: string, art: 'neu' | 'schon-da') => void;
  readonly onLadenNoetig: () => void;
}): ReactElement {
  const own = useStyles();
  const [kanal, setKanal] = useState<SuppressionChannel | undefined>(undefined);
  const [weg, setWeg] = useState<string>(ALLE);
  const [grund, setGrund] = useState<SuppressionReason | undefined>(undefined);
  const [geltung, setGeltung] = useState<SuppressionScope>('group');
  const [fehler, setFehler] = useState<{ readonly kanal?: string; readonly grund?: string }>({});
  const [meldung, setMeldung] = useState<PlainMessage | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const anfrage = useRef<{ readonly id: string; readonly schluessel: string } | undefined>(undefined);
  const meldungRef = useRef<HTMLDivElement | null>(null);
  const [fokusMeldung, setFokusMeldung] = useState(0);
  useEffect(() => {
    if (fokusMeldung > 0) meldungRef.current?.focus();
  }, [fokusMeldung]);

  const wege = kanal === 'email' ? wegOptionen(party.emailAddresses) : kanal === 'phone' ? wegOptionen(party.phoneNumbers) : [];
  const haus = data.houseScope;
  // Fokus nach einer Antwort erst nach dem Rendern: während des Sendens sind die Felder gesperrt.
  const [fokusZiel, setFokusZiel] = useState<{ readonly id: string; readonly n: number } | undefined>(undefined);
  useEffect(() => {
    if (fokusZiel === undefined) return;
    const ziel = document.getElementById(fokusZiel.id);
    (ziel?.querySelector<HTMLInputElement>('input') ?? ziel)?.focus();
  }, [fokusZiel]);
  const fokusAuf = (id: string): void => setFokusZiel((alt) => ({ id, n: (alt?.n ?? 0) + 1 }));

  const eintragen = async (): Promise<void> => {
    const neueFehler = {
      ...(kanal === undefined ? { kanal: 'Wähle E-Mail, Telefon oder Post.' } : {}),
      ...(grund === undefined ? { grund: 'Wähle einen Grund.' } : {}),
    };
    setFehler(neueFehler);
    if (kanal === undefined || grund === undefined) {
      fokusAuf(kanal === undefined ? 'sperre-kanal' : 'sperre-grund');
      return;
    }
    const gewaehlt = wege.find((option) => option.contactPointId === weg);
    const scope = geltung === 'house' && haus.available ? 'house' : 'group';
    const schluessel = JSON.stringify([kanal, gewaehlt?.contactPointId ?? '', grund, scope]);
    if (anfrage.current?.schluessel !== schluessel) anfrage.current = { id: newClientRequestId(), schluessel };
    setBusy(true);
    setMeldung(undefined);
    let ergebnis;
    try {
      const token = await accessToken();
      if (token === undefined) {
        setBusy(false);
        onReauthenticate();
        return;
      }
      ergebnis = await addSuppression({
        baseUrl: apiBaseUrl,
        accessToken: token,
        partyId: party.partyId,
        input: { channel: kanal, reasonCode: grund, scope, ...(gewaehlt === undefined ? {} : { contactPointUsageId: gewaehlt.contactPointUsageId }) },
        clientRequestId: anfrage.current.id,
      });
    } catch {
      // Ob der Server eingetragen hat, ist offen; dieselbe Kennung beim nächsten Versuch.
      setBusy(false);
      setMeldung(UNREACHABLE_MESSAGE);
      setFokusMeldung((zahl) => zahl + 1);
      onLadenNoetig();
      return;
    }
    setBusy(false);
    if (ergebnis.outcome === 'applied') {
      anfrage.current = undefined;
      const wert = gewaehlt === undefined ? undefined : (party.emailAddresses.concat(party.phoneNumbers).find((w) => w.contactPointId === gewaehlt.contactPointId)?.value);
      onEingetragen(addedAnnouncement(kanal, wert, grund, scope, haus), 'neu');
      return;
    }
    if (ergebnis.outcome === 'invalid') {
      const felder = new Set(ergebnis.errors.map((fehlerZeile) => fehlerZeile.fieldPath));
      setFehler({
        ...(felder.has('channel') ? { kanal: 'Wähle E-Mail, Telefon oder Post.' } : {}),
        ...(felder.has('reasonCode') ? { grund: 'Wähle einen Grund.' } : {}),
      });
      fokusAuf(felder.has('channel') ? 'sperre-kanal' : felder.has('reasonCode') ? 'sperre-grund' : 'sperre-weg');
      return;
    }
    anfrage.current = ergebnis.outcome === 'rejected' && ergebnis.message.retryable ? anfrage.current : undefined;
    const grundcode = ergebnis.outcome === 'conflict' ? ergebnis.reasonCode : undefined;
    if (grundcode === 'communication_suppression_duplicate') {
      onEingetragen(ergebnis.message.headline, 'schon-da');
      return;
    }
    if (grundcode === 'contact_point_not_found') {
      setWeg(ALLE);
      onKontaktNeu();
      setMeldung(ergebnis.message);
      fokusAuf('sperre-weg');
      return;
    }
    if (grundcode === 'party_not_writable') onKontaktNeu();
    // Ein Konflikt ohne eigenen Satz bekommt CONFLICT_MESSAGE, und der sagt "neu geladen":
    // der Abschnitt hinter dem Dialog laedt dafuer still nach.
    if (ergebnis.outcome === 'conflict' && (grundcode === undefined || !(grundcode in (SUPPRESSION_MESSAGES.conflicts ?? {})))) onLadenNoetig();
    setMeldung(ergebnis.message);
    setFokusMeldung((zahl) => zahl + 1);
  };

  return (
    <Dialog open modalType="modal" onOpenChange={(_, daten) => (daten.open ? undefined : onSchliessen())}>
      <DialogSurface aria-describedby={undefined}>
        <DialogBody>
          <DialogTitle>Sperre eintragen</DialogTitle>
          <DialogContent className={own.dialogInhalt}>
            {meldung !== undefined ? (
              <div ref={meldungRef} tabIndex={-1} role="alert">
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <MessageBarTitle>{meldung.headline}</MessageBarTitle>
                    <div>{meldung.explanation}</div>
                    <div>{meldung.nextStep}</div>
                  </MessageBarBody>
                </MessageBar>
              </div>
            ) : undefined}
            <Field label="Für welchen Weg?" required validationMessage={fehler.kanal ?? null} validationState={fehler.kanal === undefined ? 'none' : 'error'}>
              <RadioGroup
                id="sperre-kanal"
                layout="horizontal"
                className={own.kanal}
                value={kanal ?? ''}
                onChange={(_, daten) => {
                  setKanal(daten.value as SuppressionChannel);
                  setWeg(ALLE);
                  setFehler(({ grund: g }) => (g === undefined ? {} : { grund: g }));
                }}
              >
                {KANAELE.map((code) => (
                  <Radio key={code} value={code} label={CHANNEL_WORD[code]} disabled={busy} />
                ))}
              </RadioGroup>
            </Field>
            {kanal === 'email' || kanal === 'phone' ? (
              wege.length > 0 ? (
                <Field
                  label={kanal === 'email' ? 'Welche E-Mail-Adresse?' : 'Welche Telefonnummer?'}
                  // Die Liste am Kontakt ist ab 30 Wegen gekürzt; dann sagt es der Hinweis.
                  {...(party.additionalContactPointCount > 0 ? { hint: truncatedWaysNote(kanal) } : {})}
                >
                  <Select id="sperre-weg" className={own.wegAuswahl} value={weg} disabled={busy} onChange={(_, daten) => setWeg(daten.value)}>
                    <option value={ALLE}>{kanal === 'email' ? 'Alle E-Mail-Adressen' : 'Alle Telefonnummern'}</option>
                    {wege.map((option) => (
                      <option key={option.contactPointId} value={option.contactPointId}>
                        {option.text}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Caption1 id="sperre-weg" tabIndex={-1}>
                  {kanal === 'email' ? 'Gilt auch für E-Mail-Adressen, die später dazukommen.' : 'Gilt auch für Telefonnummern, die später dazukommen.'}
                </Caption1>
              )
            ) : undefined}
            <Field label="Grund" required validationMessage={fehler.grund ?? null} validationState={fehler.grund === undefined ? 'none' : 'error'}>
              <RadioGroup
                id="sperre-grund"
                value={grund ?? ''}
                onChange={(_, daten) => {
                  setGrund(daten.value as SuppressionReason);
                  setFehler(({ kanal: k }) => (k === undefined ? {} : { kanal: k }));
                }}
              >
                {REASONS.map((eintrag) => (
                  <Radio
                    key={eintrag.code}
                    value={eintrag.code}
                    disabled={busy}
                    label={
                      <>
                        {eintrag.wort}
                        <Caption1 className={own.grundHinweis}>{eintrag.hinweis}</Caption1>
                      </>
                    }
                  />
                ))}
              </RadioGroup>
            </Field>
            <Field label="Gilt für">
              <RadioGroup value={geltung} onChange={(_, daten) => setGeltung(daten.value as SuppressionScope)}>
                <Radio value="group" label={scopeWords('group', undefined)} disabled={busy} />
                {haus.available ? <Radio value="house" label={scopeWords('house', haus.displayName)} disabled={busy} /> : undefined}
              </RadioGroup>
            </Field>
            <Caption1 className={own.hinweisZeile}>
              <span aria-hidden="true">ⓘ</span>
              <span>{DIALOG_NOTE}</span>
            </Caption1>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" disabledFocusable={busy} onClick={() => void eintragen()}>
              {busy ? 'Wird eingetragen …' : 'Sperre eintragen'}
            </Button>
            <AbbrechenKnopf disabled={busy} onClick={onSchliessen} />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Für die Abschnitte Kontaktwege und Anschriften: die Sperren, die eine Zeile treffen. */
export interface SperrLage {
  readonly treffer: readonly SuppressionView[];
  /** Nicht geladen oder gekürzt: eine Zeile ohne Treffer kann trotzdem gesperrt sein. */
  readonly unvollstaendig: boolean;
  /** Erst geladen ist "keine Treffer" eine Aussage; während des Ladens noch nicht. */
  readonly geladen: boolean;
}

export type SperrenFuer = (channel: SuppressionChannel, contactPointId?: string) => SperrLage;

export function sperrenFuer(stand: SperrenStand): SperrenFuer {
  const sperren = stand.status === 'geladen' ? (stand.data?.suppressions ?? []) : [];
  // Während des Ladens nichts (es flackerte sonst); ein Fehler oder eine gekürzte Liste ist unvollständig.
  const unvollstaendig = stand.status === 'fehler' || (stand.status === 'geladen' && (stand.data?.additionalSuppressionCount ?? 0) > 0);
  return (channel, contactPointId) => ({
    treffer: sperren.filter((sperre) => sperre.channel === channel && (sperre.contactPointId === undefined || sperre.contactPointId === contactPointId)),
    unvollstaendig,
    geladen: stand.status === 'geladen',
  });
}

